import {InteractionPath} from '@webmate-web-core/shared/types/browsersession';

type InteractionPathLike = InteractionPath | { actions?: any[]; steps?: any[] } | null | undefined;

const URL_REGEX = /https?:\/\/[^\s,)]+/i;
const PLAYWRIGHT_PREFIX = /^Playwright action:\s*/i;

export function getStateDisplayLabel(description?: string | null, path?: InteractionPathLike): string | undefined {
    return getInteractionPathDisplayLabel(path)
        || getReadableTechnicalStateDescription(description)
        || trimToUndefined(description);
}

export function getInteractionPathDisplayLabel(path?: InteractionPathLike): string | undefined {
    if (!path) {
        return undefined;
    }

    if (Array.isArray((path as any).actions) && (path as any).actions.length > 0) {
        const lastAction = [...(path as any).actions].reverse().find(action => !!action);
        return formatSerializedPlaywrightAction(lastAction);
    }

    if (Array.isArray((path as any).steps) && (path as any).steps.length > 0) {
        const lastStep = [...(path as any).steps].reverse().find(step => !!step);
        return formatNativeInteractionStep(lastStep)
            || getReadableTechnicalStateDescription(lastStep?.description)
            || getReadableTechnicalStateDescription(typeof lastStep === 'string' ? lastStep : undefined);
    }

    return undefined;
}

export function getInteractionPathStepCount(path?: InteractionPathLike): number | undefined {
    if (Array.isArray((path as any)?.actions) && (path as any).actions.length > 0) {
        return (path as any).actions.length;
    }
    if (Array.isArray((path as any)?.steps) && (path as any).steps.length > 0) {
        return (path as any).steps.length;
    }
    return undefined;
}

export function getInteractionPathLastUrl(path?: InteractionPathLike): string | undefined {
    if (!path) {
        return undefined;
    }

    if (Array.isArray((path as any).actions)) {
        const reversedActions = [...(path as any).actions].reverse();
        for (const action of reversedActions) {
            const actionUrl = extractUrlFromSerializedAction(action);
            if (actionUrl) {
                return actionUrl;
            }
        }
    }

    if (Array.isArray((path as any).steps)) {
        const reversedSteps = [...(path as any).steps].reverse();
        for (const step of reversedSteps) {
            const stepUrl = extractFirstUrl(step?.url)
                || extractFirstUrl(step?.description)
                || extractFirstUrl(step?.value)
                || extractUrlFromSerializedAction(step);
            if (stepUrl) {
                return stepUrl;
            }
        }
    }

    return undefined;
}

export function getReadableTechnicalStateDescription(description?: string | null): string | undefined {
    const trimmedDescription = trimToUndefined(description);
    if (!trimmedDescription || !isTechnicalStateDescription(trimmedDescription)) {
        return undefined;
    }

    const normalizedDescription = trimmedDescription.replace(PLAYWRIGHT_PREFIX, '');
    const navigateUrl = normalizedDescription.match(/Navigate\((https?:\/\/[^)]+)\)/i)?.[1];
    if (navigateUrl) {
        return `Navigate to ${navigateUrl}`;
    }

    const fillValue = normalizedDescription.match(/Fill\(\"([^\"]*)\"/i)?.[1];
    if (fillValue) {
        return `Fill with "${fillValue}"`;
    }

    const typeValue = normalizedDescription.match(/Type\(([^,)]*)/i)?.[1];
    if (typeValue) {
        return `Type ${trimQuotes(typeValue)}`;
    }

    const sequentialTypeValue = normalizedDescription.match(/PressSequentially\(\"([^\"]*)\"/i)?.[1];
    if (sequentialTypeValue) {
        return `Type "${sequentialTypeValue}"`;
    }

    const pressKey = normalizedDescription.match(/Press\(([^,)]*)/i)?.[1];
    if (pressKey) {
        return `Press ${trimQuotes(pressKey)}`;
    }

    if (/Dblclick\(/i.test(normalizedDescription)) {
        return 'Double-click';
    }

    if (/Click\(/i.test(normalizedDescription)) {
        return 'Click';
    }

    if (/Hover\(/i.test(normalizedDescription)) {
        return 'Hover';
    }

    if (/Check\(/i.test(normalizedDescription)) {
        return 'Check';
    }

    if (/Uncheck\(/i.test(normalizedDescription)) {
        return 'Uncheck';
    }

    if (/Clear\(/i.test(normalizedDescription)) {
        return 'Clear';
    }

    if (/Focus\(/i.test(normalizedDescription)) {
        return 'Focus';
    }

    if (/Blur\(/i.test(normalizedDescription)) {
        return 'Blur';
    }

    if (/Tap\(/i.test(normalizedDescription)) {
        return 'Tap';
    }

    if (/DragTo\(/i.test(normalizedDescription)) {
        return 'Drag';
    }

    if (/SelectOption\(/i.test(normalizedDescription)) {
        return 'Select option';
    }

    if (/ScrollIntoViewIfNeeded\(/i.test(normalizedDescription)) {
        return 'Scroll into view';
    }

    if (/SerializedPlaywrightAction\(/.test(normalizedDescription)) {
        return 'Recorded Playwright action';
    }

    return undefined;
}

export function isTechnicalStateDescription(description?: string | null): boolean {
    const trimmedDescription = trimToUndefined(description);
    if (!trimmedDescription) {
        return false;
    }

    return PLAYWRIGHT_PREFIX.test(trimmedDescription)
        || trimmedDescription.includes('SerializedPlaywrightAction(')
        || trimmedDescription.includes('Navigate(');
}

export function extractFirstUrl(value?: string | null): string | undefined {
    const trimmedValue = trimToUndefined(value);
    if (!trimmedValue) {
        return undefined;
    }

    if (/^https?:\/\//i.test(trimmedValue)) {
        return trimmedValue;
    }

    const match = trimmedValue.match(URL_REGEX);
    return match?.[0];
}

function extractUrlFromSerializedAction(action?: any): string | undefined {
    return extractFirstUrl(action?.url)
        || extractFirstUrl(action?.actionOnLocators?.url)
        || extractFirstUrl(action?.description)
        || extractFirstUrl(typeof action === 'string' ? action : undefined);
}

function formatNativeInteractionStep(step?: any): string | undefined {
    if (!step) {
        return undefined;
    }

    const url = extractFirstUrl(step?.url)
        || extractFirstUrl(step?.value);
    if (step?.type === 'NavigateStep' || step?.type === 'Navigate' || step?.type === 'GoToUrl' || !!url) {
        return url ? `Navigate to ${url}` : 'Navigate';
    }

    return trimToUndefined(step?.description);
}

function formatSerializedPlaywrightAction(action?: any): string | undefined {
    if (!action) {
        return undefined;
    }

    if (typeof action === 'string') {
        return getReadableTechnicalStateDescription(action) || trimToUndefined(action);
    }

    const actionSpec = action?.actionOnLocators;
    const actionType = trimToUndefined(actionSpec?.type);
    if (!actionType) {
        return getReadableTechnicalStateDescription(action?.description) || trimToUndefined(action?.description);
    }

    const locator = getPrimaryLocator(action?.chainedLocators);
    const locatorLabel = formatLocator(locator);
    const actionLabel = formatActionLabel(actionType, actionSpec, locatorLabel);

    return actionLabel || humanizeIdentifier(actionType);
}

function formatActionLabel(actionType: string, actionSpec: any, locatorLabel?: string): string | undefined {
    switch (actionType) {
        case 'Navigate':
            return actionSpec?.url ? `Navigate to ${actionSpec.url}` : 'Navigate';
        case 'Click':
            return locatorLabel ? `Click ${locatorLabel}` : 'Click';
        case 'Dblclick':
            return locatorLabel ? `Double-click ${locatorLabel}` : 'Double-click';
        case 'Hover':
            return locatorLabel ? `Hover ${locatorLabel}` : 'Hover';
        case 'Fill':
            return actionSpec?.value
                ? locatorLabel ? `Fill ${locatorLabel} with "${actionSpec.value}"` : `Fill with "${actionSpec.value}"`
                : locatorLabel ? `Fill ${locatorLabel}` : 'Fill';
        case 'Type':
            return actionSpec?.text
                ? locatorLabel ? `Type "${actionSpec.text}" into ${locatorLabel}` : `Type "${actionSpec.text}"`
                : locatorLabel ? `Type into ${locatorLabel}` : 'Type';
        case 'PressSequentially':
            return actionSpec?.text
                ? locatorLabel ? `Type "${actionSpec.text}" into ${locatorLabel}` : `Type "${actionSpec.text}"`
                : locatorLabel ? `Type into ${locatorLabel}` : 'Type';
        case 'Press':
            return actionSpec?.key
                ? locatorLabel ? `Press ${actionSpec.key} on ${locatorLabel}` : `Press ${actionSpec.key}`
                : locatorLabel ? `Press key on ${locatorLabel}` : 'Press key';
        case 'SelectOption':
            return locatorLabel ? `Select option in ${locatorLabel}` : 'Select option';
        case 'Check':
            return locatorLabel ? `Check ${locatorLabel}` : 'Check';
        case 'Uncheck':
            return locatorLabel ? `Uncheck ${locatorLabel}` : 'Uncheck';
        case 'Clear':
            return locatorLabel ? `Clear ${locatorLabel}` : 'Clear';
        case 'Focus':
            return locatorLabel ? `Focus ${locatorLabel}` : 'Focus';
        case 'Blur':
            return locatorLabel ? `Blur ${locatorLabel}` : 'Blur';
        case 'Tap':
            return locatorLabel ? `Tap ${locatorLabel}` : 'Tap';
        case 'DragTo':
            return locatorLabel ? `Drag ${locatorLabel}` : 'Drag';
        case 'ScrollIntoViewIfNeeded':
            return locatorLabel ? `Scroll to ${locatorLabel}` : 'Scroll into view';
        default:
            return undefined;
    }
}

function getPrimaryLocator(locators?: any[]): any | undefined {
    if (!Array.isArray(locators) || locators.length === 0) {
        return undefined;
    }

    return [...locators].reverse().find(locator => !!locator);
}

function formatLocator(locator?: any): string | undefined {
    if (!locator) {
        return undefined;
    }

    const locatorType = trimToUndefined(locator?.type);
    const text = trimToUndefined(locator?.text);

    switch (locatorType) {
        case 'GetByRole': {
            const role = humanizeRole(locator?.role);
            const roleName = trimToUndefined(locator?.options?.name);
            if (roleName && role) {
                return `${role} "${roleName}"`;
            }
            return role || roleName || 'element';
        }
        case 'GetByLabel':
        case 'GetByLabelPattern':
            return text ? `label "${text}"` : 'labelled field';
        case 'GetByPlaceholder':
        case 'GetByPlaceholderPattern':
            return text ? `field "${text}"` : 'field';
        case 'GetByText':
        case 'GetByTextPattern':
            return text ? `text "${text}"` : 'text';
        case 'GetByTitle':
        case 'GetByTitlePattern':
            return text ? `title "${text}"` : 'title';
        case 'GetByAltText':
        case 'GetByAltTextPattern':
            return text ? `image "${text}"` : 'image';
        case 'GetByTestId':
            return locator?.testId ? `test id "${locator.testId}"` : 'test id';
        case 'Locator':
            return locator?.locator ? `selector "${locator.locator}"` : 'selector';
        default:
            return text || humanizeIdentifier(locatorType || 'element');
    }
}

function humanizeRole(role?: any): string | undefined {
    const roleValue = typeof role === 'string'
        ? role
        : trimToUndefined(role?.type) || trimToUndefined(role?.name);

    if (!roleValue) {
        return undefined;
    }

    return humanizeIdentifier(roleValue.replace(/Role$/, ''));
}

function humanizeIdentifier(value: string): string {
    return value
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .trim()
        .toLowerCase();
}

function trimQuotes(value: string): string {
    return value.replace(/^['"]|['"]$/g, '').trim();
}

function trimToUndefined(value?: string | null): string | undefined {
    if (typeof value !== 'string') {
        return undefined;
    }

    const trimmedValue = value.trim();
    return trimmedValue.length > 0 ? trimmedValue : undefined;
}
